/**
 * What a package needs from `@libs/core` and `@core/widgets`: package-specific behaviour that used to be configured
 * through `NX_PUBLIC_*` / `INIT_VARIABLES`. The package declares it in `<package>/core-options.json`; the app build
 * injects it as `CORE_OPTIONS` (see `libs/core/build/resolveCoreOptions.js`) and the core reads it with
 * `getCoreOptions()`. Every field is optional: a missing one keeps the core's default. Plain JSON data only.
 * Every field must also be registered in `OPTION_DEFINITIONS` there: the build rejects keys it does not know.
 *
 * A stand can still override a value with the matching env variable (named on each field); it wins over the package.
 *
 * Environment settings (URLs, backend, keys, logging) stay in the global variables — only what differs between
 * packages belongs here. Everything here ends up in the client bundle: never put secrets in it.
 */
export type TCoreOptions = {
    network?: {
        /** How many times a scenario request is sent before the error surfaces. Env: `NX_PUBLIC_FETCH_SCENARIO_MAX_ATTEMPTS`. Default: 3. */
        fetchScenarioMaxAttempts?: number;
        /**
         * A separate session per browser tab (auth in `sessionStorage`), when the orchestrator supports it.
         * Env: `NX_PUBLIC_SUPPORT_MULTI_TABS`. Default: `false`.
         */
        supportMultiTabs?: boolean;
        /** When the connection counts as slow (`uiaPerf` snapshot `isSlowConnection`, in-flight `isSlow`). */
        slowConnection?: {
            /**
             * Latency, ms, at or above which a request is slow: the p95 of finished requests or the age of a pending one.
             * Env: `NX_PUBLIC_SLOW_CONNECTION_P95_MS`. Default: 3000.
             */
            p95Ms?: number;
            /** `navigator.connection.effectiveType` values that count as slow. Env: `NX_PUBLIC_SLOW_CONNECTION_EFFECTIVE_TYPES`. Default: `['slow-2g', '2g']`. */
            effectiveTypes?: string[];
        };
    };
    navigation?: {
        /** Where to go after the `closeFramework` player action. Env: `NX_PUBLIC_GLOBAL_redirectURLtoCloseFramework`. Default: stay. */
        closeFrameworkRedirectUrl?: string;
        /** Path prefixes served by the deep-link page (`<path>/*`). Env: `NX_PUBLIC_GLOBAL_deepLinkPath`. Default: none. */
        deepLinkPaths?: string[];
    };
    theme?: {
        /** Force the light scheme regardless of the user preference and the OS. Env: `NX_PUBLIC_GLOBAL_disableDarkTheme`. Default: `false`. */
        disableDarkTheme?: boolean;
    };
    animation?: {
        /**
         * Animate page changes the backend declares with `uiEffects` (React `<ViewTransition>`): the store holds back
         * subscriber notifications while a page change renders as a transition. Only the consumer package plays them.
         * Env: `NX_PUBLIC_PAGE_TRANSITIONS` (`true` / `false`). Default: `false`.
         */
        pageTransitions?: boolean;
    };
    image?: {
        /**
         * Icons that keep their own colours instead of being tinted through the SVG mask (multicolour brand icons).
         * Each entry is matched as a substring of the image `src`, e.g. a file name or a path fragment.
         * Env: `NX_PUBLIC_GLOBAL_iconsWithoutColorMask`. Default: none.
         */
        iconsWithoutColorMask?: string[];
    };
};
